import React, { type FC, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Order, DeliveryFormData, Address } from '../types';
import CreateDeliveryModal from '../components/CreateDeliveryModal';
import ConfirmModal from '../components/ConfirmModal.tsx';
import { useCustomers } from '../hooks/useCustomers';
import { assignDriverToOrderInDb, unassignDriverFromOrderInDb } from '../services/deliveryService';
import ActiveDeliveriesTable from '../components/ActiveDeliveriesTable.tsx';
import { useHomeData } from '../hooks/useHomeData';
import OrderRow from "../components/OrderRow.tsx";
import { ORDER_STATUS } from '../constants/orderStatuses';

interface HomeProps { title?: string; }

// Shape of each KPI stat card shown in the overview grid.
interface Card {
  title: string;
  value: number | string;
  link:  string;
  // Optional router state passed to the target page (pre-filling status filters).
  state?: Record<string, unknown>;
}

// Locale-aware number formatter for stat card values.
const fmt = (n: number) => new Intl.NumberFormat('en-ZA').format(n);
const formatDate = (d?: string | null) => d ? new Date(d).toLocaleString() : '–';

const Home: FC<HomeProps> = () => {
  const { fetchAddressesForCustomer } = useCustomers();
  const navigate = useNavigate();

  // ── Data ───────────────────────────────────────────────────────────────────

  const {
    openOrderCount,
    activeDeliveryCount,
    totalDeliveryCount,
    awaitingPackingCount,
    packerAssignedCount,
    ordersNeedingDriver,
    ordersWithActiveDeliveries,
    pendingAcceptance,
    refresh,
  } = useHomeData();

  // ── UI state ───────────────────────────────────────────────────────────────

  // Shared loading flag for the assign and unassign mutations.
  const [loading, setLoading] = useState(false);

  // Assign-driver modal state.
  const [isCreateDeliveryModalOpen, setIsCreateDeliveryModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [newDelivery, setNewDelivery] = useState<DeliveryFormData>({
    destination_id: '',
    driver_id:      '',
  });

  // Unassign confirmation modal state.
  const [isUnassignModalOpen, setIsUnassignModalOpen] = useState(false);
  const [orderToUnassign,     setOrderToUnassign]     = useState<string | number | null>(null);

  // ── Stat card definitions ──────────────────────────────────────────────────

  // The Packing card passes router state, so clicking "View" pre-filters the
  // order page to AWAITING_PACKING and PACKER_ASSIGNED statuses.
  const cards: Card[] = [
    { title: 'Open Orders', value: openOrderCount, link: '/orders' },
    { title: 'Active Deliveries', value: activeDeliveryCount, link: '/activedeliveries' },
    { title: 'Deliveries', value: totalDeliveryCount,  link: '/deliveries' },
    {
      title: 'Packing (Awaiting / Assigned)',
      value: `${awaitingPackingCount} / ${packerAssignedCount}`,
      link:  '/orders',
      state: { statuses: [ORDER_STATUS.AWAITING_PACKING, ORDER_STATUS.PACKER_ASSIGNED] },
    },
  ];

  // ── Handlers ───────────────────────────────────────────────────────────────

  const goToOrder = (id: string | number) => navigate(`/orders/${id}`);
  // Navigates to the order page with state that triggers create modal to open.
  const goToNewOrder = () => navigate('/orders', { state: { openCreate: true } });

  // Opens the assign-driver modal for a given order, pre-filling the destination
  // address and fetching all addresses on file for the customer.
  const openAssignFor = async (order: Order) => {
    setSelectedOrder(order);
    const initialAddressId = order.delivery_address_id || order.customer?.delivery_address || null;
    setNewDelivery({ destination_id: initialAddressId, driver_id: null });
    if (order.customer_id) {
      const fetched = await fetchAddressesForCustomer(String(order.customer_id));
      setAddresses(fetched);
    }
    setIsCreateDeliveryModalOpen(true);
  };

  // Submits the assign-driver form. On success, closes the modal and
  // refreshes the dashboard so the order moves out of the "needs driver" list.
  const handleDeliverySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrder || !newDelivery.destination_id || !newDelivery.driver_id) return;
    try {
      setLoading(true);
      const success = await assignDriverToOrderInDb(
          String(selectedOrder.id),
          selectedOrder.customer_id,
          newDelivery.destination_id,
          newDelivery.driver_id,
      );
      if (success) {
        setIsCreateDeliveryModalOpen(false);
        setSelectedOrder(null);
        await refresh();
      }
    } finally {
      setLoading(false);
    }
  };

  // Stores the target order ID and opens the confirmation modal.
  // `e.stopPropagation()` prevents the row click (goToOrder) from also firing.
  const triggerUnassignModal = (e: React.MouseEvent, orderId: string | number) => {
    e.stopPropagation();
    setOrderToUnassign(orderId);
    setIsUnassignModalOpen(true);
  };

  // Confirmed unassign button: removes the driver from the order, closes the modal,
  // and refreshes so the order reappears in the "needs driver" list.
  const handleConfirmUnassign = async () => {
    if (!orderToUnassign) return;
    try {
      setLoading(true);
      await unassignDriverFromOrderInDb(orderToUnassign);
      setIsUnassignModalOpen(false);
      setOrderToUnassign(null);
      await refresh();
    } catch {
      alert('Failed to unassign driver.');
    } finally {
      setLoading(false);
    }
  };

  // Shared Tailwind class for all table header cells.
  const TH = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500';

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
      <div className="space-y-8">
        <header>
          <h1 className="text-2xl font-semibold text-stone-900">Overview</h1>
        </header>

        {/* Stat cards — KPI grid showing open orders, deliveries, and packing queue counts */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((c, i) => {
            const isOpenOrders = c.title === 'Open Orders';
            return (
                <div key={i} className="group rounded-2xl bg-white border border-stone-200 shadow-sm p-4 transition hover:shadow-md hover:-translate-y-[1px]">
                  <div className="text-sm text-stone-500">{c.title}</div>
                  <div className="mt-2 text-2xl font-medium tracking-tight text-stone-900">
                    {/* Numbers use the locale formatter; composite values (e.g. "3 / 7") are rendered as-is */}
                    {typeof c.value === 'number' ? fmt(c.value) : c.value}
                  </div>
                  <div className="mt-3 h-px bg-stone-200" />
                  <div className="mt-3 text-xs text-stone-600 flex items-center gap-2">
                    {/* "View" navigates to the relevant page, optionally with pre-set filter state */}
                    <Link to={c.link} state={c.state}
                          className="inline-flex items-center rounded-full border border-stone-300 px-2 py-0.5 hover:bg-stone-100 transition-colors">
                      View
                    </Link>
                    {/* "New" shortcut only appears on the Open Orders card */}
                    {isOpenOrders && (
                        <button onClick={goToNewOrder}
                                className="inline-flex items-center rounded-full px-2 py-0.5 bg-[#ee2b74] text-white hover:bg-[#d4245f] transition-colors">
                          New
                        </button>
                    )}
                    <span className="ml-auto opacity-0 group-hover:opacity-100 transition">→</span>
                  </div>
                </div>
            );
          })}
        </section>

        {/* Assign a driver — orders in PACKING_COMPLETE / ASSIGN_DRIVER / DELIVERY_FAILED
                that are ready for a driver but don't have one yet */}
        <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
          <div className="px-5 py-3 border-b border-stone-200">
            <h2 className="font-semibold text-stone-900">Assign a Driver to an Order</h2>
          </div>
          {ordersNeedingDriver === null ? (
              <div className="p-4 text-sm text-stone-400">Loading…</div>
          ) : ordersNeedingDriver.length === 0 ? (
              <div className="p-4 text-sm text-stone-500">No pending assignments.</div>
          ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className={TH}>Order Ref</th>
                    <th className={TH}>Customer</th>
                    <th className={TH}>Type</th>
                    <th className={TH}>Status</th>
                    <th className={TH}>Created</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Action</th>
                  </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                  {ordersNeedingDriver.map(order => (
                      <OrderRow key={order.id} order={order} onRowClick={goToOrder}
                                onAssign={o => void openAssignFor(o)} />
                  ))}
                  </tbody>
                </table>
              </div>
          )}
        </section>

        {/* Pending driver acceptance — orders in DRIVER_ASSIGNED status where the
                driver has been notified but has not yet accepted the delivery in the app */}
        <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
          <div className="px-5 py-3 border-b border-stone-200">
            <h2 className="font-semibold text-stone-900">Pending Driver Acceptance</h2>
          </div>
          {pendingAcceptance === null ? (
              <div className="p-4 text-sm text-stone-400">Loading…</div>
          ) : pendingAcceptance.length === 0 ? (
              <div className="p-4 text-sm text-stone-500">No orders waiting for driver acceptance.</div>
          ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className={TH}>Order Ref</th>
                    <th className={TH}>Customer</th>
                    <th className={TH}>Assigned Driver</th>
                    <th className={TH}>Time Elapsed</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Action</th>
                  </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                  {pendingAcceptance.map(order => (
                      <tr key={order.id} onClick={() => goToOrder(order.id)}
                          className="cursor-pointer hover:bg-stone-50 transition">
                        <td className="px-4 py-3 font-medium text-stone-900">{order.order_reference}</td>
                        <td className="px-4 py-3 text-stone-700">{order.customer}</td>
                        {/* Animated amber dot indicates the driver has been notified but not yet responded */}
                        <td className="px-4 py-3">
                                        <span className="border border-stone-300 px-2 py-0.5 text-xs text-stone-700 rounded-full inline-flex items-center gap-1.5">
                                            <span className="h-2 w-2 rounded-full bg-[#f5ab40] animate-pulse" />
                                          {order.driver_name}
                                        </span>
                        </td>
                        <td className="px-4 py-3 text-stone-500 text-xs">{formatDate(order.assigned_date)}</td>
                        <td className="px-4 py-3 text-right">
                          {/* Unassign opens a confirmation modal rather than acting immediately */}
                          <button onClick={e => triggerUnassignModal(e, order.id)} disabled={loading}
                                  className="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-full hover:bg-red-100 transition disabled:opacity-50">
                            {loading ? '…' : 'Unassign'}
                          </button>
                        </td>
                      </tr>
                  ))}
                  </tbody>
                </table>
              </div>
          )}
        </section>

        {/* Active deliveries — orders in ON_ROUTE status, delegated to its own component */}
        <ActiveDeliveriesTable orders={ordersWithActiveDeliveries} />

        {/* Unassign confirmation modal — shown before removing a driver from an order */}
        <ConfirmModal
            isOpen={isUnassignModalOpen}
            title="Unassign Driver"
            message="Are you sure you want to remove this driver from the order?"
            onConfirm={handleConfirmUnassign}
            onCancel={() => { setIsUnassignModalOpen(false); setOrderToUnassign(null); }}
            loading={loading}
        >
          <div className="flex items-center gap-3 text-stone-600 rounded-lg">
            <svg className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-xs font-medium">
              The order will be moved back to the 'Assign Driver' list, and the driver will no longer see it in their app.
            </p>
          </div>
        </ConfirmModal>

        {/* Assign-driver modal — opened via openAssignFor from the "needs driver" table */}
        {isCreateDeliveryModalOpen && (
            <CreateDeliveryModal
                isOpen={isCreateDeliveryModalOpen}
                onClose={() => setIsCreateDeliveryModalOpen(false)}
                newDelivery={newDelivery}
                onDeliveryChange={e => setNewDelivery(p => ({ ...p, destination_id: e.target.value }))}
                onDriverChange={e => setNewDelivery(p => ({ ...p, driver_id: e.target.value }))}
                onSubmit={handleDeliverySubmit}
                loading={loading}
                customerAddresses={addresses}
                selectedOrder={selectedOrder}
            />
        )}
      </div>
  );
};

export default Home;